/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import { 
  collection, 
  addDoc, 
  doc, 
  updateDoc,
  query, 
  where, 
  getDocs, 
  orderBy
} from 'firebase/firestore';
import { db, auth } from '../firebase';

// New order structure
export interface PayoutOrder {
  id: string;
  number: string;
  amount: number;
}

export interface PayoutRecord {
  id: string;
  userId: string; // Admin who created it (for tracking)
  userEmail: string;
  collectionTitle: string;
  collectionHandle?: string; // NEW: Collection handle for matching
  orders: PayoutOrder[]; // UPDATED: Combined structure
  date: string;
  // The highest order number included in this payout. Used instead of
  // `date` as the reference point for "what's unpaid since the last
  // payment" - order numbers are plain integers with no timezone or
  // string-format ambiguity, unlike `date` (free-text MM-DD-YYYY, which
  // is exactly what caused the Chrome-vs-Safari dashboard mismatch).
  // Optional only because older payouts created before this field
  // existed need a backfill (see backfill-payout-collection-handles.js)
  // before it's populated.
  lastOrderNumberPaid?: number;
  amount: number;
  method: string;
  confirmationNumber: string;
  notes: string;
  createdAt: string;
  createdBy: string;
  status: 'pending' | 'completed' | 'cancelled';
}

// Shared by createPayout and the admin backfill tool so "highest order
// number in this payout" is computed identically in both places.
export const computeLastOrderNumberPaid = (orders: { number: string }[]): number | undefined => {
  const numbers = orders
    .map((order) => parseInt(String(order.number).replace('#', ''), 10))
    .filter((n) => !isNaN(n));

  return numbers.length > 0 ? Math.max(...numbers) : undefined;
};

/**
 * Create a new payout record
 */

export const createPayout = async (payoutData: {
  userId: string;
  userEmail: string;
  collectionTitle: string;
  collectionHandle?: string;
  orders: PayoutOrder[];
  date: string;
  amount: number;
  method: string;
  confirmationNumber: string;
  notes: string;
  createdBy: string;
}): Promise<string> => {
  try {
    console.log('🔐 Current user auth state:', {
      uid: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified
    });
    
    // Validate that we have orders
    if (!payoutData.orders || payoutData.orders.length === 0) {
      throw new Error('Cannot create payout without orders');
    }
    
    // Validate that all orders have amounts
    const ordersWithAmounts = payoutData.orders.every(order => 
      typeof order.amount === 'number' && !isNaN(order.amount)
    );
    
    if (!ordersWithAmounts) {
      console.warn('⚠️ Some orders are missing amount data');
      payoutData.orders.forEach((order, idx) => {
        if (typeof order.amount !== 'number' || isNaN(order.amount)) {
          console.warn(`  Order ${idx}: #${order.number} (ID: ${order.id}) - Missing or invalid amount`);
        }
      });
    }
    
    // Log the amounts for debugging
    console.log('💰 Order amounts being stored:', 
      payoutData.orders.map(o => ({ number: o.number, amount: o.amount }))
    );
    
    // Helper function to round to 2 decimal places
    const roundToTwo = (num: number): number => {
      return Math.round((num + Number.EPSILON) * 100) / 100;
    };
    
    // Create clean data object (Firestore doesn't like undefined values)
    const cleanData = {
      userId: payoutData.userId || '',
      userEmail: payoutData.userEmail || '',
      collectionTitle: payoutData.collectionTitle || '',
      collectionHandle: payoutData.collectionHandle || '',
      orders: payoutData.orders.map(order => ({
        id: order.id,
        number: order.number,
        amount: roundToTwo(Number(order.amount) || 0) // Round to 2 decimals
      })),
      date: payoutData.date || new Date().toISOString(),
      // 0 rather than undefined - Firestore's client SDK rejects
      // undefined field values, and the "no valid numeric order numbers"
      // case can't happen in practice (orders.length > 0 was already
      // validated above, and order.number always comes from a real
      // Shopify order number).
      lastOrderNumberPaid: computeLastOrderNumberPaid(payoutData.orders) ?? 0,
      amount: roundToTwo(payoutData.amount || 0), // Round to 2 decimals
      method: payoutData.method || 'check',
      confirmationNumber: payoutData.confirmationNumber || '',
      notes: payoutData.notes || '',
      createdAt: new Date().toISOString(),
      createdBy: payoutData.createdBy || 'Unknown',
      status: 'completed' as const
    };
    
    console.log('📝 Creating payout with rounded amounts:', cleanData.orders);
    
    const payoutRef = await addDoc(collection(db, 'payouts'), cleanData);
    
    console.log('✅ Payout created with ID:', payoutRef.id);
    console.log('✅ Orders stored with rounded amounts:', cleanData.orders);
    return payoutRef.id;
  } catch (error) {
    console.error('❌ Error creating payout:', error);
    throw error;
  }
};

/**
 * UPDATED: Get all payouts for a user based on their collection handles
 * Now supports both handles and titles for backward compatibility
 * Automatically deduplicates if a payout appears in multiple collections
 */
export const getPayoutsByUserCollections = async (
  collectionHandles: string[]
): Promise<PayoutRecord[]> => {
  if (!collectionHandles || collectionHandles.length === 0) {
    return [];
  }

  try {
    const payoutsRef = collection(db, 'payouts');
    
    // Firestore 'in' operator supports max 10 items, so we need to chunk
    const chunks = [];
    for (let i = 0; i < collectionHandles.length; i += 10) {
      chunks.push(collectionHandles.slice(i, i + 10));
    }
    
    // Use a Map to deduplicate by payout ID
    const payoutsMap = new Map<string, PayoutRecord>();
    
    for (const chunk of chunks) {
      // Query by collectionHandle first (new way). No orderBy here
      // deliberately - results get merged across chunks and re-sorted by
      // createdAt below regardless, so an orderBy on the query itself did
      // nothing except require a Firestore composite index for
      // where('collectionHandle','in',...) + orderBy('createdAt') that
      // may never have existed. That combination had never actually been
      // exercised for a non-admin caller until the stricter payouts rule
      // started requiring this query to be properly scoped - if the
      // index was missing, getDocs below throws, which the catch below
      // silently swallows, falling through to the (separately broken)
      // title fallback and surfacing as "no payouts found" with no
      // visible error. Dropping orderBy removes that failure mode
      // entirely: a single-field 'in' filter needs no composite index.
      const handleQuery = query(
        payoutsRef,
        where('collectionHandle', 'in', chunk)
      );
      console.log('🔍 Attempting to query by collectionHandle:', chunk);
      try {
        const handleSnapshot = await getDocs(handleQuery);
        handleSnapshot.forEach((doc) => {
          if (!payoutsMap.has(doc.id)) {
            const data = doc.data();
            
            // MIGRATION: Handle old data format (orderIds/orderNumbers) and new format (orders)
            let orders: PayoutOrder[] = [];
            if (data.orders) {
            // New format - ensure amounts are numbers
            orders = data.orders.map((order: any) => ({
                id: order.id,
                number: order.number,
                amount: typeof order.amount === 'number' ? order.amount : 0
            }));
            } else if (data.orderIds && data.orderNumbers) {
            // Old format - convert without amounts
            orders = data.orderIds.map((id: string, index: number) => ({
                id,
                number: data.orderNumbers[index] || '',
                amount: 0 // Default for old records that don't have amounts
            }));
            }
            
            payoutsMap.set(doc.id, {
              id: doc.id,
              ...data,
              orders
            } as PayoutRecord);
          }
        });
      } catch (error) {
        console.log('❌ collectionHandle query error:', error);
  console.log('No index for collectionHandle yet, trying collectionTitle fallback');
        console.log('No index for collectionHandle yet, trying collectionTitle fallback');
      }
      
      // FALLBACK: Also query by collectionTitle for backward compatibility
      // This handles old payouts that don't have collectionHandle yet.
      // No orderBy here either - same reasoning as the handle query above.
      const titleQuery = query(
        payoutsRef,
        where('collectionTitle', 'in', chunk)
      );
      
      try {
        const titleSnapshot = await getDocs(titleQuery);
        titleSnapshot.forEach((doc) => {
          if (!payoutsMap.has(doc.id)) {
            const data = doc.data();
            
            let orders: PayoutOrder[] = [];
            if (data.orders) {
              orders = data.orders;
            } else if (data.orderIds && data.orderNumbers) {
              orders = data.orderIds.map((id: string, index: number) => ({
                id,
                number: data.orderNumbers[index] || ''
              }));
            }
            
            payoutsMap.set(doc.id, {
              id: doc.id,
              ...data,
              orders
            } as PayoutRecord);
          }
        });
      } catch (error) {
        console.error('Error querying by collectionTitle:', error);
      }
    }
    
    // Convert map to array and sort
    const allPayouts = Array.from(payoutsMap.values());
    allPayouts.sort((a, b) => 
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    
    console.log(`Found ${allPayouts.length} unique payouts for collection handles:`, collectionHandles);
    return allPayouts;
  } catch (error) {
    console.error('Error fetching payouts by collections:', error);
    throw error;
  }
};

// Fetches the payout docs a caller is allowed to read, scoped to their own
// collection(s) when handles are given - matching the Firestore rule for
// non-admins (resource.data.collectionHandle must be one of the caller's
// own collectionHandles). An unfiltered query(payoutsRef) is rejected
// outright for non-admins: Firestore can't verify every document in an
// unconstrained collection query satisfies a per-document field condition,
// so the whole query fails rather than silently returning a filtered
// subset. Pass [] for an admin caller, who the rule allows to read every
// payout regardless of query shape - this keeps the original unscoped
// behavior for them.
const fetchAccessiblePayouts = async (
  collectionHandles: string[]
): Promise<PayoutRecord[]> => {
  if (collectionHandles.length > 0) {
    return getPayoutsByUserCollections(collectionHandles);
  }

  const payoutsRef = collection(db, 'payouts');
  const snapshot = await getDocs(query(payoutsRef));

  return snapshot.docs.map((doc) => {
    const data = doc.data();

    // MIGRATION: Handle both old and new formats
    let orders: PayoutOrder[] = [];
    if (data.orders) {
      orders = data.orders;
    } else if (data.orderIds && data.orderNumbers) {
      orders = data.orderIds.map((id: string, index: number) => ({
        id,
        number: data.orderNumbers[index] || '',
        amount: 0
      }));
    }

    return { id: doc.id, ...data, orders } as PayoutRecord;
  });
};

/**
 * Get payouts by order IDs
 * Used to fetch full payout details for specific orders
 */
export const getPayoutsByOrderIds = async (
  orderIds: string[],
  collectionHandles: string[] = []
): Promise<PayoutRecord[]> => {
  if (!orderIds || orderIds.length === 0) {
    return [];
  }

  try {
    const payouts = await fetchAccessiblePayouts(collectionHandles);

    const allPayouts = payouts.filter((payout) =>
      (payout.orders || []).some((order) => orderIds.includes(order.id))
    );

    console.log(`Found ${allPayouts.length} payouts for ${orderIds.length} order IDs`);
    return allPayouts;
  } catch (error) {
    console.error('Error fetching payouts by order IDs:', error);
    throw error;
  }
};

export const updatePayout = async (payoutId: string, updates: Partial<PayoutRecord>) => {
  const payoutRef = doc(db, 'payouts', payoutId);
  await updateDoc(payoutRef, updates);
};

/**
 * Get total payout amount for user across all their collections
 */
export const getTotalPayoutAmountByCollections = async (
  collectionHandles: string[]
): Promise<number> => {
  const payouts = await getPayoutsByUserCollections(collectionHandles);
  return payouts.reduce((sum, payout) => sum + (payout.amount || 0), 0);
};

/**
 * Get payout summary grouped by collection
 */
export const getPayoutSummaryByCollection = async (
  collectionHandles: string[]
): Promise<Map<string, { count: number; total: number; payouts: PayoutRecord[] }>> => {
  const payouts = await getPayoutsByUserCollections(collectionHandles);
  const summary = new Map<string, { count: number; total: number; payouts: PayoutRecord[] }>();
  
  payouts.forEach(payout => {
    const collectionKey = payout.collectionTitle; // Use title for display
    const existing = summary.get(collectionKey) || { count: 0, total: 0, payouts: [] };
    summary.set(collectionKey, {
      count: existing.count + 1,
      total: existing.total + payout.amount,
      payouts: [...existing.payouts, payout]
    });
  });
  
  return summary;
};

/**
 * KEPT FOR BACKWARDS COMPATIBILITY: Get payouts by userId (for admins only)
 * Regular users should use getPayoutsByUserCollections instead
 */
export const getPayoutsByUser = async (userId: string): Promise<PayoutRecord[]> => {
  try {
    const payoutsRef = collection(db, 'payouts');
    const q = query(
      payoutsRef,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const payouts: PayoutRecord[] = [];
    
    snapshot.forEach((doc) => {
      const data = doc.data();
      
      // MIGRATION: Handle both formats
      let orders: PayoutOrder[] = [];
      if (data.orders) {
        orders = data.orders;
      } else if (data.orderIds && data.orderNumbers) {
        orders = data.orderIds.map((id: string, index: number) => ({
          id,
          number: data.orderNumbers[index] || ''
        }));
      }
      
      payouts.push({
        id: doc.id,
        ...data,
        orders
      } as PayoutRecord);
    });
    
    return payouts;
  } catch (error) {
    console.error('Error fetching payouts by user:', error);
    throw error;
  }
};

/**
 * Check which orders have been paid (by order numbers)
 */
export const checkOrdersPaidStatus = async (
  orderIds: string[],
  orderNumbers: string[],
  collectionHandles: string[] = []
): Promise<Map<string, boolean>> => {
  const paidStatus = new Map<string, boolean>();

  if (orderNumbers.length === 0) {
    return paidStatus;
  }

  try {
    const payouts = await fetchAccessiblePayouts(collectionHandles);

    const allPaidOrderNumbers = new Set<string>();
    payouts.forEach((payout) => {
      (payout.orders || []).forEach((order) => {
        if (order.number) {
          allPaidOrderNumbers.add(String(order.number));
        }
      });
    });

    orderIds.forEach((orderId, index) => {
      const orderNumber = orderNumbers[index];
      paidStatus.set(orderId, allPaidOrderNumbers.has(orderNumber));
    });

    return paidStatus;
  } catch (error) {
    console.error('Error checking order paid status:', error);
    throw error;
  }
};